import AppInfoFooter from "@/components/AppInfoFooter";
import Button from "@/components/Button";
import { TextInput } from "@/components/InputFields";
import Link from "@/components/Link";
import MaterialIcon from "@/components/MaterialIcon";
import Page from "@/components/Page";
import { useLogInWithMfaCodeMutation } from "@/data/queries";
import { useAuthSessionAtom } from "@/data/store";
import { BackendErrorCode, backendErrorCodeFor } from "@/model/BackendErrors";
import { verificationCodeRegex } from "@/model/Constants";
import { MfaChallenge } from "@/model/LoginResponse";
import cn from "@/util/cn";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router";

export type VerifyCodePageStateParams = {
  challenge: MfaChallenge;
  email?: string;
};

type VerifyCodeFormData = {
  code: string;
};

const sessionExpiredNotice = "Your verification code has expired. Please sign in again.";

const validateCode = (code: string) =>
  verificationCodeRegex.test(code) || "Please enter the 6-digit code";

const codeSentSentenceFor = ({ method, codeDestination }: MfaChallenge) => {
  switch (method) {
    case "SMS_MFA":
      return `We sent a code by text message to ${codeDestination ?? "your phone"}.`;
    case "EMAIL_OTP":
      return `We sent a code by email to ${codeDestination ?? "your email address"}.`;
  }
};

type SignInAgainLinkProps = {
  email?: string;
  children: string;
};

const SignInAgainLink = ({ email, children }: SignInAgainLinkProps) => (
  <Link to="/login" replace={true} state={{ email }} className="underline">
    {children}
  </Link>
);

type VerifyCodeErrorMessageProps = {
  errorCode: BackendErrorCode;
  email?: string;
};

const VerifyCodeErrorMessage = ({ errorCode, email }: VerifyCodeErrorMessageProps) => {
  switch (errorCode) {
    case "INVALID_CODE":
      return "The code is incorrect. Please check it and try again.";
    case "LOGIN_FAILED":
      return (
        <>
          We could not verify your code. Please&nbsp;
          <SignInAgainLink email={email}>sign in again</SignInAgainLink>.
        </>
      );
    default:
      return (
        <>
          Something went wrong. Please&nbsp;
          <SignInAgainLink email={email}>sign in again</SignInAgainLink>.
        </>
      );
  }
};

const VerifyCodePage = () => {
  const locationState = useLocation().state as VerifyCodePageStateParams | null;
  const [authSession] = useAuthSessionAtom();
  const navigate = useNavigate();
  const { mutate: logInWithMfaCode, isPending, error: logInError } = useLogInWithMfaCodeMutation();
  const {
    register,
    handleSubmit,
    trigger: validate,
    formState,
  } = useForm<VerifyCodeFormData>({
    mode: "onBlur",
    defaultValues: { code: "" },
  });

  if (!locationState) {
    return <Navigate to="/login" replace={true} />;
  }

  const { challenge, email } = locationState;
  const isLoggedIn = authSession !== null;
  const logInErrorCode = backendErrorCodeFor(logInError);
  const { errors, touchedFields } = formState;

  const onSubmit = ({ code }: VerifyCodeFormData) => {
    logInWithMfaCode(
      { challenge, code },
      {
        onSuccess: () => navigate("/", { replace: true }),
        onError: (error) => {
          if (backendErrorCodeFor(error) === "SESSION_EXPIRED") {
            navigate("/login", { replace: true, state: { email, notice: sessionExpiredNotice } });
          }
        },
      }
    );
  };

  return (
    <>
      {isLoggedIn && <Navigate to="/" replace={true} />}
      <Page className="bg-gray-50">
        <div className="flex h-full flex-col justify-between">
          <div
            className={cn("mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl")}
          >
            <h2 className="mb-4 text-center text-4xl text-primary/90">
              Enter your verification code
            </h2>
            <h3 className="mb-8 text-pretty text-center text-lg text-primary/90">
              {codeSentSentenceFor(challenge)}
            </h3>
            {logInErrorCode && logInErrorCode !== "SESSION_EXPIRED" && (
              <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                <span className="material-icons me-4 text-red-600">error</span>
                <p className="text-red-600">
                  <VerifyCodeErrorMessage errorCode={logInErrorCode} email={email} />
                </p>
              </div>
            )}
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              <label htmlFor="code" className="block leading-6 text-primary/90">
                Verification code
              </label>
              <TextInput
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="mt-2 text-center text-2xl tracking-[0.5em]"
                disabled={isPending}
                {...register("code", {
                  setValueAs: (value: string) => value.trim(),
                  required: "Please enter the 6-digit code",
                  validate: validateCode,
                  onChange: () => {
                    if (touchedFields.code) {
                      validate("code");
                    }
                  },
                })}
              />
              {errors?.code && (
                <p className="mt-2 text-sm italic text-red-600">{errors.code.message}</p>
              )}
              <p className="mt-2 text-sm text-primary/90">
                The code is valid for a few minutes. Didn&apos;t get it?&nbsp;
                <SignInAgainLink email={email}>Sign in again</SignInAgainLink> to request a new one.
              </p>
              <Button
                variant="primary"
                type="submit"
                disabled={isPending}
                className="mt-8 w-full text-base"
              >
                Verify
              </Button>
              <Link
                to="/login"
                state={{ email }}
                replace={true}
                className="mt-4 flex items-center self-center hover:no-underline"
              >
                <MaterialIcon iconName="chevron_left" />
                Back to sign in
              </Link>
            </form>
          </div>
          <AppInfoFooter className="mt-auto p-4" />
        </div>
      </Page>
    </>
  );
};

export default VerifyCodePage;
